<template>
  <div class="auth-page">
    <!-- Background decoration -->
    <div class="auth-bg">
      <div class="auth-bg-shape shape-1"></div>
      <div class="auth-bg-shape shape-2"></div>
      <div class="auth-bg-shape shape-3"></div>
    </div>

    <div class="auth-container">
      <!-- Left side - Branding -->
      <div class="auth-branding">
        <div class="branding-content">
          <div class="brand-logo">
            <img src="@/assets/img/testpal-full.png" alt="TestPal" class="logo-img" />
          </div>
          <h1 class="brand-title">TestPal Admin</h1>
          <p class="brand-subtitle">Powerful test management platform for educators and institutions</p>
          
          <div class="brand-features">
            <div class="feature-item">
              <div class="feature-icon">
                <vue-feather type="check-circle" size="20" />
              </div>
              <span>Create and manage exams effortlessly</span>
            </div>
            <div class="feature-item">
              <div class="feature-icon">
                <vue-feather type="users" size="20" />
              </div>
              <span>Collaborate with your team</span>
            </div>
            <div class="feature-item">
              <div class="feature-icon">
                <vue-feather type="bar-chart-2" size="20" />
              </div>
              <span>Track performance analytics</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Right side - Login form -->
      <div class="auth-form-wrapper">
        <div class="auth-form-container">
          <div class="auth-form-header">
            <h2>Welcome back</h2>
            <p>Sign in to your account to continue</p>
          </div>

          <form @submit.prevent="handleLogin" class="auth-form">
            <!-- Email field -->
            <div class="form-group">
              <label class="form-label">
                <vue-feather type="mail" size="16" />
                Email address
              </label>
              <div class="input-wrapper" :class="{ 'focused': emailFocused, 'has-value': form.email }">
                <input
                  v-model="form.email"
                  type="email"
                  class="form-input"
                  placeholder="Enter your email"
                  required
                  autocomplete="email"
                  @focus="emailFocused = true"
                  @blur="emailFocused = false"
                />
              </div>
            </div>

            <!-- Password field -->
            <div class="form-group">
              <label class="form-label">
                <vue-feather type="lock" size="16" />
                Password
              </label>
              <div class="input-wrapper" :class="{ 'focused': passwordFocused, 'has-value': form.password }">
                <input
                  v-model="form.password"
                  :type="showPassword ? 'text' : 'password'"
                  class="form-input"
                  placeholder="Enter your password"
                  required
                  autocomplete="current-password"
                  @focus="passwordFocused = true"
                  @blur="passwordFocused = false"
                />
                <button 
                  type="button" 
                  class="password-toggle"
                  @click="showPassword = !showPassword"
                  tabindex="-1"
                >
                  <vue-feather :type="showPassword ? 'eye-off' : 'eye'" size="18" />
                </button>
              </div>
            </div>

            <!-- Remember me & Forgot password -->
            <div class="form-options">
              <label class="checkbox-wrapper">
                <input type="checkbox" v-model="rememberMe" />
                <span class="checkmark"></span>
                <span class="checkbox-label">Remember me</span>
              </label>
              <router-link to="/forgot-password" class="forgot-link">
                Forgot password?
              </router-link>
            </div>

            <!-- Error message -->
            <Transition name="shake">
              <div v-if="error" class="error-message">
                <vue-feather type="alert-circle" size="16" />
                <span>{{ error }}</span>
              </div>
            </Transition>

            <!-- Submit button -->
            <button 
              type="submit" 
              class="submit-btn"
              :class="{ 'loading': loading }"
              :disabled="loading || !isFormValid"
            >
              <span v-if="loading" class="btn-loader"></span>
              <span class="btn-text">{{ loading ? 'Signing in...' : 'Sign In' }}</span>
              <vue-feather v-if="!loading" type="arrow-right" size="18" class="btn-icon" />
            </button>
          </form>

          <!-- Footer -->
          <div class="auth-footer">
            <p class="security-note">
              <vue-feather type="shield" size="14" />
              Protected by enterprise-grade security
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useNotifications } from '@/composables/useNotifications'

export default {
  name: 'LoginPage',
  setup() {
    const router = useRouter()
    const authStore = useAuthStore()
    const { toast } = useNotifications()

    const form = ref({ email: '', password: '' })
    const showPassword = ref(false)
    const rememberMe = ref(false)
    const loading = ref(false)
    const error = ref('')
    const emailFocused = ref(false)
    const passwordFocused = ref(false)

    const isFormValid = computed(() => {
      return form.value.email && form.value.password
    })

    const handleLogin = async () => {
      if (!isFormValid.value) {
        toast.warning('Please fill in all required fields')
        return
      }

      loading.value = true
      error.value = ''

      try {
        const result = await authStore.login(form.value.email, form.value.password)
        
        if (result.requiresSetup2FA) {
          toast.info('Please set up two-factor authentication to secure your account')
          router.push({ name: 'setup-two-factor' })
        } else if (result.requires2FA) {
          toast.info('Please enter your verification code')
          router.push({ name: 'two-factor-auth' })
        } else {
          toast.success(`Welcome back, ${authStore.user?.fullName || 'Admin'}!`)
          router.push({ name: 'dashboard' })
        }
      } catch (err) {
        error.value = err
        toast.error(err || 'Invalid credentials. Please try again.')
      } finally {
        loading.value = false
      }
    }

    return {
      form,
      showPassword,
      rememberMe,
      loading,
      error,
      emailFocused,
      passwordFocused,
      isFormValid,
      handleLogin
    }
  }
}
</script>

<style scoped>
/* Base layout */
.auth-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  position: relative;
  overflow: hidden;
  padding: 20px;
}

/* Background shapes */
.auth-bg {
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
}

.auth-bg-shape {
  position: absolute;
  border-radius: 50%;
  opacity: 0.5;
}

.shape-1 {
  width: 600px;
  height: 600px;
  background: linear-gradient(135deg, rgba(0, 48, 73, 0.08) 0%, rgba(0, 48, 73, 0.02) 100%);
  top: -200px;
  right: -100px;
  animation: float 20s ease-in-out infinite;
}

.shape-2 {
  width: 400px;
  height: 400px;
  background: linear-gradient(135deg, rgba(252, 191, 73, 0.1) 0%, rgba(252, 191, 73, 0.02) 100%);
  bottom: -100px;
  left: -100px;
  animation: float 15s ease-in-out infinite reverse;
}

.shape-3 {
  width: 300px;
  height: 300px;
  background: linear-gradient(135deg, rgba(142, 202, 230, 0.1) 0%, rgba(142, 202, 230, 0.02) 100%);
  top: 50%;
  left: 30%;
  animation: float 18s ease-in-out infinite;
}

@keyframes float {
  0%, 100% { transform: translate(0, 0) rotate(0deg); }
  25% { transform: translate(20px, -20px) rotate(5deg); }
  50% { transform: translate(-10px, 20px) rotate(-5deg); }
  75% { transform: translate(-20px, -10px) rotate(3deg); }
}

/* Main container */
.auth-container {
  display: flex;
  width: 100%;
  max-width: 1000px;
  background: #fff;
  border-radius: 24px;
  box-shadow: 
    0 4px 6px -1px rgba(0, 0, 0, 0.05),
    0 10px 15px -3px rgba(0, 0, 0, 0.08),
    0 20px 25px -5px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  position: relative;
  z-index: 1;
}

/* Branding section */
.auth-branding {
  flex: 1;
  background: linear-gradient(135deg, #003049 0%, #073b4c 100%);
  padding: 48px;
  display: flex;
  align-items: center;
  position: relative;
  overflow: hidden;
}

.auth-branding::before {
  content: '';
  position: absolute;
  inset: 0;
  background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  opacity: 0.5;
}

.branding-content {
  position: relative;
  z-index: 1;
  color: #fff;
}

.brand-logo {
  margin-bottom: 24px;
}

.logo-img {
  height: 48px;
}

.brand-title {
  font-size: 32px;
  font-weight: 700;
  margin-bottom: 12px;
  letter-spacing: -0.02em;
  color: #ffffff;
}

.brand-subtitle {
  font-size: 16px;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.6;
  margin-bottom: 40px;
}

.brand-features {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.9);
}

.feature-icon {
  width: 36px;
  height: 36px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #ffffff;
}

/* Form section */
.auth-form-wrapper {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px;
}

.auth-form-container {
  width: 100%;
  max-width: 380px;
}

.auth-form-header {
  margin-bottom: 32px;
  text-align: center;
}

.auth-form-header h2 {
  font-size: 28px;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 8px;
  letter-spacing: -0.02em;
}

.auth-form-header p {
  color: #6b7280;
  font-size: 15px;
}

/* Form styles */
.auth-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
}

.input-wrapper {
  position: relative;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  transition: all 0.2s ease;
  background: #fff;
}

.input-wrapper.focused {
  border-color: #003049;
  box-shadow: 0 0 0 4px rgba(0, 48, 73, 0.08);
}

.input-wrapper.has-value:not(.focused) {
  border-color: #d1d5db;
}

.form-input {
  width: 100%;
  padding: 14px 16px;
  border: none;
  background: transparent;
  font-size: 15px;
  color: #1f2937;
  outline: none;
  border-radius: 10px;
}

.form-input::placeholder {
  color: #9ca3af;
}

.password-toggle {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease;
}

.password-toggle:hover {
  color: #6b7280;
}

/* Form options */
.form-options {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.checkbox-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 14px;
  color: #6b7280;
}

.checkbox-wrapper input {
  display: none;
}

.checkmark {
  width: 18px;
  height: 18px;
  border: 2px solid #d1d5db;
  border-radius: 5px;
  position: relative;
  transition: all 0.2s ease;
}

.checkbox-wrapper input:checked + .checkmark {
  background: #003049;
  border-color: #003049;
}

.checkbox-wrapper input:checked + .checkmark::after {
  content: '';
  position: absolute;
  left: 5px;
  top: 2px;
  width: 5px;
  height: 9px;
  border: solid #fff;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.forgot-link {
  font-size: 14px;
  color: #003049;
  font-weight: 500;
  text-decoration: none;
  transition: color 0.2s ease;
}

.forgot-link:hover {
  color: #073b4c;
  text-decoration: underline;
}

/* Error message */
.error-message {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 10px;
  color: #dc2626;
  font-size: 14px;
}

.shake-enter-active {
  animation: shake 0.5s ease;
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  20%, 60% { transform: translateX(-8px); }
  40%, 80% { transform: translateX(8px); }
}

/* Submit button */
.submit-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 16px 24px;
  background: linear-gradient(135deg, #003049 0%, #073b4c 100%);
  color: #fff;
  border: none;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.submit-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0, 48, 73, 0.3);
}

.submit-btn:active:not(:disabled) {
  transform: translateY(0);
}

.submit-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.submit-btn.loading {
  pointer-events: none;
}

.btn-loader {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.btn-icon {
  transition: transform 0.2s ease;
}

.submit-btn:hover:not(:disabled) .btn-icon {
  transform: translateX(4px);
}

/* Footer */
.auth-footer {
  margin-top: 32px;
  text-align: center;
}

.security-note {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #9ca3af;
}

/* Responsive */
@media (max-width: 768px) {
  .auth-container {
    flex-direction: column;
    max-width: 440px;
  }

  .auth-branding {
    padding: 32px;
  }

  .brand-title {
    font-size: 24px;
  }

  .brand-subtitle {
    font-size: 14px;
    margin-bottom: 24px;
  }

  .brand-features {
    display: none;
  }

  .auth-form-wrapper {
    padding: 32px;
  }

  .auth-form-header h2 {
    font-size: 24px;
  }
}

/* Dark mode */
:global(body.dark-mode) .auth-page {
  background: linear-gradient(135deg, #111827 0%, #1f2937 100%);
}

:global(body.dark-mode) .auth-container {
  background: #1f2937;
  box-shadow: 
    0 4px 6px -1px rgba(0, 0, 0, 0.2),
    0 10px 15px -3px rgba(0, 0, 0, 0.3);
}

:global(body.dark-mode) .auth-form-header h2 {
  color: #f3f4f6;
}

:global(body.dark-mode) .auth-form-header p {
  color: #9ca3af;
}

:global(body.dark-mode) .form-label {
  color: #d1d5db;
}

:global(body.dark-mode) .input-wrapper {
  background: #374151;
  border-color: #4b5563;
}

:global(body.dark-mode) .input-wrapper.focused {
  border-color: #60a5fa;
  box-shadow: 0 0 0 4px rgba(96, 165, 250, 0.15);
}

:global(body.dark-mode) .form-input {
  color: #f3f4f6;
}

:global(body.dark-mode) .form-input::placeholder {
  color: #6b7280;
}

:global(body.dark-mode) .checkbox-wrapper {
  color: #9ca3af;
}

:global(body.dark-mode) .checkmark {
  border-color: #4b5563;
}

:global(body.dark-mode) .forgot-link {
  color: #60a5fa;
}

:global(body.dark-mode) .error-message {
  background: rgba(239, 68, 68, 0.1);
  border-color: rgba(239, 68, 68, 0.3);
}

:global(body.dark-mode) .submit-btn {
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
}

:global(body.dark-mode) .checkbox-label {
  color: #d1d5db;
}

:global(body.dark-mode) .security-note {
  color: #6b7280;
}

:global(body.dark-mode) .password-toggle {
  color: #6b7280;
}

:global(body.dark-mode) .password-toggle:hover {
  color: #9ca3af;
}
</style>
